<template>
    <div class="main-container overflow-auto">
        <v-container class="edit-panel overflow-auto pa-5">
            <v-form ref="form" @input="$emit('input', $event)">
                <!-- image info -->
                <v-row v-if="imageRec" class="mt-0 mb-5" dense>
                    <v-col cols="12">
                        <h4>{{ $t('titelbild') }}</h4>
                    </v-col>
                </v-row>
                <v-row v-if="imageRec" class="mt-2" dense>
                    <v-col cols="12">
                        <LabelField :label="$t('alt_text_desc')">
                            <span>{{ imageRec.title }}</span>
                        </LabelField>
                    </v-col>
                </v-row>
                <v-row v-if="imageRec" class="mt-2" dense>
                    <v-col cols="12">
                        <LabelField :label="$t('copyright')">
                            <span>&copy; {{ imageRec.credits }}</span>
                        </LabelField>
                    </v-col>
                </v-row>
                <v-row v-if="imageRec" class="mt-2" dense>
                    <v-col cols="12">
                        <LabelField :label="$t('caption_alt')">{{ imageRec.caption }}</LabelField>
                    </v-col>
                </v-row>
                <!-- add / change image -->
                <v-row class="mt-2" dense>
                    <v-col cols="12">
                        <h4>{{ imageRec ? $t('change_cover_image') : $t('upload_cover_image') }}</h4>
                    </v-col>
                </v-row>
                <v-row dense>
                    <v-col cols="12">
                        <!-- change img infos btn -->
                        <ActionBtn v-if="imageRec" width="200px" class="ma-1" @click.stop="showSimpleDetailDlg = true">
                            <KpIcon icon-cls="pencil" color="white" disabled-color="grey" />
                            {{ $t('change_image_info') }}
                        </ActionBtn>
                        <!-- crop img btn -->
                        <ActionBtn
                            v-if="imageRec"
                            width="200px"
                            class="ma-1"
                            @click.stop="
                                initialImage = imageRec;
                                showImgCropDlgOnOpen = true;
                                imgSelectDlg = true;
                            "
                        >
                            <KpIcon icon-cls="scissor" color="white" disabled-color="grey" left />
                            {{ $t('change_titleimg') }}
                        </ActionBtn>
                        <!-- choose img btn -->
                        <ActionBtn
                            class="ma-1"
                            width="200px"
                            @click.stop="
                                initialImage = null;
                                showImgCropDlgOnOpen = false;
                                imgSelectDlg = true;
                            "
                        >
                            <KpIcon icon-cls="mediamanager" color="white" disabled-color="grey" left />
                            {{ $t('choose_titleimg') }}
                        </ActionBtn>
                    </v-col>
                </v-row>
                <v-row dense>
                    <v-col cols="12">
                        <v-responsive max-width="400" max-height="300" :aspect-ratio="3 / 2">
                            <ImageUploadCard
                                width="100%"
                                height="100%"
                                :upload-params="{ event_organizer_id: record.organizer_id }"
                                @uploaded="afterImageUploaded"
                            />
                        </v-responsive>
                    </v-col>
                </v-row>
                <v-row dense>
                    <v-col cols="12">
                        <!-- TODO: Freie Bilddatenbank -->
                        <!-- free img database btn -->
                        <!--
                        <SecondaryBtn
                            class="ma-1"
                            @click.stop="
                                initialImage = null;
                                showImgCropDlgOnOpen = false;
                                imgSelectDlg = true;
                            "
                        >
                            <KpIcon icon-cls="arrowrightup" color="primary" disabled-color="grey" left />
                            {{ $t('free_image_database') }}
                        </SecondaryBtn>
                        -->
                    </v-col>
                </v-row>
            </v-form>
        </v-container>

        <div class="info-panel pa-5">
            <!-- info -->
            <KpAlert v-if="!imageRec" type="info">
                <span v-html="$t('event_cover_image_requirements')" />
            </KpAlert>
            <!-- image crops -->
            <div v-if="imageRec" class="images-container">
                <h4>{{ $t('img_formats') }}</h4>
                <div v-for="(conf, key) in imageConfig.event" :key="key" class="image-container mb-5">
                    <label class="tgk-paragraph-text">{{ conf.text }} ({{ conf.width }}x{{ conf.height }})</label>
                    <v-img
                        :max-width="conf.width"
                        class="mt-3"
                        :src="getImageUrl(record, conf)"
                        :alt="conf.text"
                        :title="conf.text"
                    >
                        <img
                            v-if="conf.overlay_preview"
                            alt="overlay image"
                            style="position: absolute; left: 0; top: 0; width: 100%; height: 100%"
                            :src="globalVar('webroot') + conf.overlay_preview"
                            class="overlaypreviewimage"
                        />
                    </v-img>
                </div>
            </div>
        </div>
        <SimpleImageDetailDlg
            v-if="imageRec"
            v-model="showSimpleDetailDlg"
            :image-record="imageRec"
            @save="onImageEdit"
        />
        <MMSelectDialog
            v-if="imgSelectDlg"
            v-model="imgSelectDlg"
            :image-formats-config="imageConfig.event"
            :image-crop-data="initialImage ? record.featured_img_crop_config : null"
            :initial-image="initialImage"
            :initial-step="showImgCropDlgOnOpen ? 2 : 1"
            :organizer-id="userIsStaff() ? record.organizer_id : null"
            @image-select="onImageSelect"
            @image-crop="onImageCrop"
        />
    </div>
</template>

<script>
import { mapActions } from 'vuex';
import { Event } from '@/models/Event';
import GlobalsMixin from '@/mixins/globals';
import MMSelectDialog from '@/components/media_manager/SelectFormatDialog.vue';
import ImageUploadCard from '@/components/ImageUploadCard.vue';
import { errToStr } from '@/helpers/tools';
import { userIsStaff } from '@/helpers/auth';
import { Image } from '@/models/Image';
import SimpleImageDetailDlg from '@/components/event/SimpleImageDetailDlg.vue';
import { commitToast } from '@/plugins/toast';

export default {
    name: 'EventEditTitelbild',
    components: {
        SimpleImageDetailDlg,
        MMSelectDialog,
        ImageUploadCard,
    },
    mixins: [GlobalsMixin],

    props: {
        record: {
            type: Event,
            required: true,
        },
    },

    data() {
        return {
            showSimpleDetailDlg: false,
            showImgCropDlgOnOpen: false,
            imgSelectDlg: false,
            initialImage: null,
            cacheBuster: new Date().getTime(),
        };
    },

    computed: {
        imageRec() {
            return this.$store.getters['image/imageById'](this.record.featured_image_id);
        },
    },

    watch: {
        record(record) {
            if (record && record.featured_image_id) {
                this.loadImage({ imageId: record.featured_image_id });
            }
        },
    },

    created() {
        // static view variables:
        this.imageConfig = this.globalVar('featured_images_config') || {};
    },

    mounted() {
        this.loadImage({ imageId: this.record.featured_image_id });
        this.validateForm();
    },

    methods: {
        userIsStaff,
        ...mapActions('image', ['loadImage']),

        onInput(field, value) {
            this.record.set(field, value);
        },

        validateForm() {
            this.$refs.form.validate();
        },

        getImageUrl(eventRec, imageConf) {
            return `${this.globalVar('imageroot')}/event/${eventRec.id}/${imageConf.ident}.${imageConf.format}?ts=${
                this.cacheBuster
            }`;
        },

        async onImageEdit() {
            this.showSimpleDetailDlg = false;
            if (!this.imageRec.getId()) {
                return;
            }
            try {
                this.$emit('saving', true);
                await this.imageRec.save();
                this.cacheBuster = new Date().getTime();
                this.$emit('featured-image-created');
            } catch (e) {
                commitToast({ message: errToStr(e), color: 'error' });
            } finally {
                this.$emit('saving', false);
            }
        },

        async afterImageUploaded(images, flowFiles) {
            if (images && images.length) {
                const img = new Image(images[0]);
                await this.onImageCrop(null, img);
            }
        },

        async onImageSelect(image) {
            if (image) {
                await this.onImageCrop({}, image);
            }
        },

        async onImageCrop(cropData, image) {
            if (image) {
                try {
                    this.imgSelectDlg = false;
                    this.$emit('saving', true);
                    await this.createFeaturedImage(cropData, image, this.record);
                    this.record.set({
                        featured_image_id: image.id,
                        featured_img_crop_config: cropData || null,
                    });
                    this.cacheBuster = new Date().getTime();
                    this.loadImage({ imageId: image.id });
                    this.$emit('featured-image-created');
                } catch (e) {
                    commitToast({ message: errToStr(e), color: 'error' });
                } finally {
                    this.$emit('saving', false);
                }
            }
        },

        async createFeaturedImage(cropData, image, record) {
            await image.createFeaturedImage('event', cropData, record.id);
        },
    },
};
</script>

<style lang="scss" scoped>
.main-container {
    display: grid;
    grid-template-columns: 1fr minmax(300px, 2fr);
    grid-template-areas: 'edit info';
}

.edit-panel {
    grid-area: edit;
}

.info-panel {
    grid-area: info;
    background-color: var(--toolbar-bg-color);
}

.images-container {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
}

.image-container {
    width: 100%;
}

label,
.v-textarea :deep(textarea) {
    color: var(--gray-text-color);
}
</style>
